import { useTranslation } from "react-i18next";
import { PortalBalanceResponse } from "../../features/portal/contracts/PortalBalanceResponse";
import { ContractAcceptanceStatus } from "../../features/contracts/models/ContractAcceptanceStatus";
import { formatMoney } from "../../features/billing/utils/money";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";
import { fallbackPaymentStatusStyle, paymentStatusStyles } from "./paymentStatusStyles";

interface PortalBalanceCardProps {
  balance: PortalBalanceResponse | undefined;
  /** False where the full payments list is shown beside the card (Money). */
  showRecentPayments?: boolean;
}

/** Read-only contracts + recent payments for one member. */
const PortalBalanceCard = ({ balance, showRecentPayments = true }: PortalBalanceCardProps) => {
  const { t, i18n } = useTranslation();

  if (!balance) {
    return <p className="text-sm text-grey-40 dark:text-grey-80">{t("common.loading", "Loading…")}</p>;
  }

  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(
      new Date(`${date}T00:00:00`),
    );

  return (
    <div className="flex flex-col gap-4">
      {showRecentPayments && balance.contracts.length === 0 && balance.recentPayments.length === 0 && (
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {t("portal.noBilling", "No billing information yet.")}
        </p>
      )}

      {balance.contracts.map((contract) => (
        <div key={contract.id} className="with-border rounded-2xl bg-white p-3 dark:bg-primary-20">
          <p className="font-medium text-grey-20 dark:text-white">
            {t(`portal.contractType.${contract.type}`, contract.type)}
          </p>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {contract.packSize != null && t("portal.packVisits", "{{count}} visits", { count: contract.packSize })}
            {contract.monthlyFeeAmountMinorUnits != null &&
              contract.monthlyFeeCurrency &&
              `${formatMoney(contract.monthlyFeeAmountMinorUnits, contract.monthlyFeeCurrency, getDisplayLocale())} / ${t("portal.month", "month")}`}
          </p>
          {contract.acceptanceStatus === ContractAcceptanceStatus.Accepted && contract.acceptedAt && (
            <p className="mt-1 text-xs text-green-700 dark:text-green-400">
              {t("portal.acceptance.acceptedOn", "Accepted {{date}}", {
                date: formatDate(contract.acceptedAt.slice(0, 10)),
              })}
            </p>
          )}
        </div>
      ))}

      {showRecentPayments && balance.recentPayments.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-grey-40 dark:text-grey-80">
            {t("portal.recentPayments", "Recent payments")}
          </p>
          <ul className="flex flex-col gap-2">
            {balance.recentPayments.map((payment) => (
              <li
                key={payment.id}
                className="with-border flex items-center justify-between rounded-2xl bg-white p-3 dark:bg-primary-20"
              >
                <div>
                  <p className="font-medium text-grey-20 dark:text-white">
                    {formatMoney(payment.amountMinorUnits, payment.amountCurrency, getDisplayLocale())}
                  </p>
                  <p className="text-sm text-grey-40 dark:text-grey-80">
                    {payment.remainingBalance != null
                      ? payment.remainingBalance === 0
                        ? t("portal.packUsedUp", "Pack used up")
                        : t("portal.visitsLeft", "{{count}} visits left", {
                            count: payment.remainingBalance,
                          })
                      : payment.dueDate
                        ? `${t("portal.due", "Due")} ${formatDate(payment.dueDate)}`
                        : formatDate(payment.dateCreated.slice(0, 10))}
                  </p>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    paymentStatusStyles[payment.status] ?? fallbackPaymentStatusStyle
                  }`}
                >
                  {t(`portal.paymentStatus.${payment.status}`, payment.status)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PortalBalanceCard;
